import type { WithNotifications } from '../models/traits/WithNotifications.js'
import type { WithUIConfiguration } from '../models/traits/WithUIConfiguration.js'
import type {
  EntityReference,
  Task,
  TaskActionButton,
  TaskAssigneeType,
  TaskListAction,
  TaskUIConfig,
} from '../types/task.js'

/**
 * Check if a task has a specific trait
 * @param task The task to check
 * @param traitName Name of the trait to check for
 */
export function hasTrait(task: Task, traitName: string): boolean {
  if (!task || !task.traits || !Array.isArray(task.traits)) {
    return false
  }
  return task.traits.includes(traitName)
}

/**
 * Safely call a task method if it exists
 * @param task The task to call method on
 * @param methodName Name of the method to call
 * @param args Arguments to pass to the method
 * @returns The result of the method call or undefined if method doesn't exist
 */
export function callTaskMethod<T>(task: Task, methodName: string, ...args: unknown[]): T | undefined {
  if (typeof task[methodName] === 'function') {
    // console.warn(`Calling method ${methodName} on task`, task)
    return (task[methodName] as (...args: unknown[]) => T)(...args)
  }
  return undefined
}

/**
 * Type guard to check if a task has UI configuration methods
 */
export function hasUIConfiguration(task: Task): task is Task & WithUIConfiguration {
  return hasTrait(task, 'WithUIConfiguration')
    && typeof task.getUIConfig === 'function'
    && typeof task.getTaskIcon === 'function'
    && typeof task.getTaskColor === 'function'
}

/**
 * Type guard to check if a task has assignment methods
 */
export function hasAssignment(task: Task): task is Task & {
  assignedToId?: string
  assignedToType?: TaskAssigneeType
  assign: (entityId: string, entityType: TaskAssigneeType) => void
  isAssignedTo: (entityId: string, entityType: TaskAssigneeType) => boolean
} {
  return hasTrait(task, 'WithAssignment')
}

/**
 * Type guard to check if a task has deadline methods
 */
export function hasDeadline(task: Task): task is Task & {
  dueDate?: Date
  completedAt?: Date
  setDueDate: (date: Date) => void
  markCompleted: () => void
  isOverdue: () => boolean
} {
  return hasTrait(task, 'WithDeadline')
}

/**
 * Type guard to check if a task has reference tracking methods
 */
export function hasReferences(task: Task): task is Task & {
  references: EntityReference[]
  addReference: (entityId: string, entityType: string, referenceType: string) => Task
  removeReference: (entityId: string, entityType: string, referenceType?: string) => Task
  hasReference: (entityId: string, entityType: string, referenceType?: string) => boolean
  getReferencesByType: (referenceType: string) => EntityReference[]
  getReferencesByEntityType: (entityType: string) => EntityReference[]
} {
  return hasTrait(task, 'WithReferences')
}

/**
 * Type guard to check if a task has notification capabilities
 */
export function hasNotifications(task: Task): task is Task & WithNotifications {
  return hasTrait(task, 'WithNotifications')
    && typeof task.notifyStatusChanged === 'function'
    && typeof task.notifyCreated === 'function'
    && typeof task.notifyAssigned === 'function'
}

/**
 * Get UI configuration for a task, with fallbacks for tasks without the trait
 */
export function getTaskUIConfig(task: Task): TaskUIConfig {
  if (hasUIConfiguration(task) && typeof task.getUIConfig === 'function') {
    return task.getUIConfig()
  }

  // Fallback for tasks without UI config
  return {
    icon: getTaskIcon(task),
    color: getTaskColor(task),
    actionButtons: [],
    listActions: [],
  }
}

/**
 * Get task icon with fallback
 */
export function getTaskIcon(task: Task): string {
  if (hasUIConfiguration(task)) {
    return task.getTaskIcon()
  }

  const icons = {
    member: 'pi pi-user',
    subkrewe: 'pi pi-users',
    admin: 'pi pi-cog',
    system: 'pi pi-server',
  }
  return icons[task.taskType] || 'pi pi-check-circle'
}

/**
 * Get task color with fallback
 */
export function getTaskColor(task: Task): string {
  if (hasUIConfiguration(task)) {
    return task.getTaskColor()
  }

  const colors = {
    low: 'var(--blue-500)',
    medium: 'var(--primary-color)',
    high: 'var(--orange-500)',
    critical: 'var(--red-500)',
  }
  return colors[task.priority] || 'var(--primary-color)'
}

/**
 * Get task list actions with fallback
 */
export function getTaskListActions(task: Task): TaskListAction[] {
  if (hasUIConfiguration(task)) {
    return task.getListActions()
  }
  return []
}

/**
 * Get task action buttons with fallback
 */
export function getTaskActionButtons(task: Task): TaskActionButton[] {
  if (hasUIConfiguration(task)) {
    return task.getActionButtons()
  }
  return []
}
